import { useMemo, useState } from "react";
import { BUSINESS, MEDIA } from "@/data";
import { SectionHeading, CallButton, QuoteButton } from "./Buttons";
import * as Icons from "lucide-react";

// "Not All Back Glass Repairs Are the Same" — educational repair-method explainer.

const BG_MEDIA = {
  housing: "https://customer-assets.emergentagent.com/job_unlock-miami/artifacts/f7w3k0n9_WITHFRAME3.PNG",
  housingDetail: "https://customer-assets.emergentagent.com/job_unlock-miami/artifacts/xi7s4ni9_WITHFRAME2.PNG",
  glassOnly: "https://customer-assets.emergentagent.com/job_unlock-miami/artifacts/5hijhf7w_GLASS%20ONLY.PNG",
};

const RECOMMENDED_MODELS = [
  "iPhone 11", "iPhone 11 Pro", "iPhone 11 Pro Max",
  "iPhone 12", "iPhone 12 Pro", "iPhone 12 Pro Max",
  "iPhone 13", "iPhone 13 Pro", "iPhone 13 Pro Max",
  "iPhone 14 Pro", "iPhone 14 Pro Max",
];

const GLASS_ONLY_MODELS = [
  "iPhone 14", "iPhone 14 Plus",
  "iPhone 15", "iPhone 15 Plus", "iPhone 15 Pro", "iPhone 15 Pro Max",
  "iPhone 16", "iPhone 16 Plus", "iPhone 16 Pro", "iPhone 16 Pro Max",
  "iPhone 17", "iPhone 17 Plus", "iPhone 17 Pro", "iPhone 17 Pro Max",
];

// Pre-built lookup so the selector knows which method to recommend per model.
const MODEL_REPAIR_MAP = {
  ...Object.fromEntries(RECOMMENDED_MODELS.map((m) => [m, "housing"])),
  ...Object.fromEntries(GLASS_ONLY_MODELS.map((m) => [m, "glass"])),
};

const ALL_MODELS = [...RECOMMENDED_MODELS, ...GLASS_ONLY_MODELS];

export default function BackGlass({ onOpenQuote }) {
  return (
    <section
      id="back-glass"
      data-testid="back-glass-section"
      className="relative bg-white py-20 md:py-28 px-4 md:px-8 overflow-hidden"
    >
      <div className="absolute -top-20 -right-32 w-96 h-96 rounded-full bg-green-100/60 blur-[120px]" aria-hidden />
      <div className="absolute -bottom-20 -left-32 w-96 h-96 rounded-full bg-orange-100/50 blur-[120px]" aria-hidden />

      <div className="relative max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="Back Glass Repair"
          title={<>Not all back glass repairs<br /><span className="text-brand-red">are the same.</span></>}
          sub="At Your Phone Plug, we repair your iPhone back glass the right way — based on how your specific model was built."
          testid="back-glass-heading"
          align="center"
        />

        {/* Trust strip */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto" data-testid="back-glass-trust-strip">
          {[
            { icon: "Award", label: "Original Apple Parts" },
            { icon: "Layers", label: "Factory Fit" },
            { icon: "ShieldCheck", label: "Stronger Repair" },
            { icon: "Smartphone", label: "Model-Specific" },
          ].map((b, i) => {
            const Icon = Icons[b.icon] || Icons.CheckCircle2;
            return (
              <div key={i} data-testid={`bg-trust-${i}`} className="flex items-center gap-2.5 px-4 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-green-50 grid place-items-center shrink-0">
                  <Icon className="w-4 h-4 text-brand-red" />
                </div>
                <span className="text-xs md:text-sm font-bold text-brand-navy">{b.label}</span>
              </div>
            );
          })}
        </div>

        {/* Interactive iPhone model selector */}
        <ModelSelector onOpenQuote={onOpenQuote} />

        {/* Comparison cards */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          {/* RECOMMENDED — Whole Frame + Housing */}
          <div data-testid="bg-card-housing" className="relative bg-white border-2 border-brand-red rounded-3xl shadow-2xl glow-red p-7 md:p-9 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-green-100/80 blur-2xl pointer-events-none" />
            <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-red text-white text-[10px] font-bold uppercase tracking-widest shadow-md z-10">
              <Icons.Star className="w-3 h-3 fill-white" />
              Recommended
            </div>

            <div className="relative">
              {/* Visual — full housing with attached glass */}
              <div className="relative -mx-2 mb-6 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 via-white to-green-50/40 border border-slate-100" data-testid="bg-housing-image">
                <div className="aspect-[16/10] flex items-center justify-center p-3">
                  <img
                    src={BG_MEDIA.housing}
                    alt="Full iPhone housing with factory-attached back glass — split showing exterior and interior"
                    className="max-h-full max-w-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl bg-white/85 backdrop-blur-md border border-white shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-brand-navy">Original Apple housing</span>
                  </div>
                  <span className="text-[10px] font-bold text-brand-red">Glass = factory-attached</span>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-200 mb-4 w-fit">
                <Icons.Layers className="w-3.5 h-3.5 text-brand-red" />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--brand-red-dark)" }}>The right way</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-black text-brand-navy tracking-tight leading-tight">
                Whole Frame + Housing<br />Replacement
              </h3>
              <p className="text-sm text-slate-600 mt-2 font-medium">
                For iPhone 11, 12, 13, 14 Pro &amp; 14 Pro Max
              </p>

              <ul className="mt-6 space-y-3">
                {[
                  "Original Apple housing & frame",
                  "Preowned, hand-picked 9/10 or 10/10 condition",
                  "Back glass already attached from the factory",
                  "Cleaner fit and stronger finish",
                  "Better long-term repair quality",
                  "Looks & feels closer to original",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-brand-red grid place-items-center shrink-0 mt-0.5">
                      <Icons.Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-sm md:text-base text-slate-700">{t}</span>
                  </li>
                ))}
              </ul>

              {/* Eligible models */}
              <div className="mt-7 pt-6 border-t border-slate-100">
                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Available for</span>
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="bg-housing-models">
                  {RECOMMENDED_MODELS.map((m) => (
                    <span key={m} className="px-2.5 py-1 rounded-md bg-green-50 border border-green-200 text-[11px] font-semibold text-brand-red">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* NOT RECOMMENDED — Glass-only on older models */}
          <div data-testid="bg-card-glass-only" className="relative bg-slate-50 border-2 border-slate-200 rounded-3xl p-7 md:p-9 overflow-hidden">
            <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-widest z-10">
              <Icons.AlertTriangle className="w-3 h-3" />
              Not for older body styles
            </div>

            <div className="relative">
              {/* Visual — glass + adhesive only */}
              <div className="relative -mx-2 mb-6 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-100 via-white to-slate-100 border border-slate-200" data-testid="bg-glass-image">
                <div className="aspect-[16/10] flex items-center justify-center p-3">
                  <img
                    src={BG_MEDIA.glassOnly}
                    alt="Aftermarket back glass and 3M adhesive sheet — the components used in a glue-only repair"
                    className="max-h-full max-w-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl bg-white/85 backdrop-blur-md border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Aftermarket glass</span>
                  </div>
                  <span className="text-[10px] font-bold text-amber-600">+ 3M adhesive only</span>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 mb-4 w-fit">
                <Icons.Droplets className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-slate-600">Glue-only method</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-black text-slate-700 tracking-tight leading-tight">
                Back Glass Only<br />Replacement
              </h3>
              <p className="text-sm text-slate-500 mt-2 font-medium">
                Common at other shops — but not the right fit for these models
              </p>

              <ul className="mt-6 space-y-3">
                {[
                  "Usually aftermarket glass",
                  "Manually glued onto the frame",
                  "May not sit perfectly like factory glass",
                  "More likely to crack again from small drops",
                  "Lower quality vs. original housing replacement",
                  "Some stores offer this — we don't, for these models",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-slate-300 grid place-items-center shrink-0 mt-0.5">
                      <Icons.X className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-sm md:text-base text-slate-600">{t}</span>
                  </li>
                ))}
              </ul>

              {/* Visual diagram */}
              <div className="mt-7 pt-6 border-t border-slate-200">
                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">How it goes wrong</span>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <div className="flex-1 px-3 py-2 rounded-lg bg-white border border-slate-200 text-center">
                    <div className="font-bold text-slate-700">New glass</div>
                    <div className="text-[10px] text-slate-400">aftermarket</div>
                  </div>
                  <Icons.Plus className="w-3 h-3 text-slate-400 shrink-0" />
                  <div className="flex-1 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-center">
                    <div className="font-bold text-amber-700">Hand glue</div>
                    <div className="text-[10px] text-amber-600">weak bond</div>
                  </div>
                  <Icons.ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                  <div className="flex-1 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-center">
                    <div className="font-bold text-red-600">Cracks again</div>
                    <div className="text-[10px] text-red-500">first drop</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Honest callout */}
        <div className="mt-8 bg-brand-navy text-white rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-start gap-5 shadow-xl" data-testid="bg-honest-callout">
          <div className="w-12 h-12 rounded-2xl bg-brand-red/20 border border-brand-red/40 grid place-items-center shrink-0">
            <Icons.Info className="w-6 h-6 text-brand-red" />
          </div>
          <div>
            <h4 className="font-display text-lg md:text-xl font-black">Why we don't recommend glass-only repairs for older models</h4>
            <p className="text-sm md:text-base text-white/80 mt-2 leading-relaxed">
              Many repair shops remove only the broken glass and glue on a new aftermarket back glass.
              The problem is that these parts are not factory-installed, the glue doesn't bond the same way,
              and the glass can break again much easier. That's why we prefer replacing the full original
              housing when the phone model requires it.
            </p>
          </div>
        </div>

        {/* Second part — newer iPhones */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-testid="bg-newer-section">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 mb-4">
              <Icons.Sparkles className="w-3.5 h-3.5 text-orange-600" />
              <span className="text-xs font-bold uppercase tracking-widest text-orange-700">Newer Models · Different Build</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-brand-navy tracking-tight leading-[1.05]">
              For newer iPhones,<br />
              <span className="text-brand-red">back glass only repair makes sense.</span>
            </h3>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
              For iPhone 14, iPhone 14 Plus, iPhone 15 series, and iPhone 16 series, Apple changed the way these phones are built. On these models, the back glass is actually <strong className="text-brand-navy">designed to be removable</strong>.
            </p>
            <p className="mt-3 text-base md:text-lg text-slate-600 leading-relaxed">
              Because of that, we can replace the back glass only on these models while still keeping the repair clean, safe, and reliable.
            </p>

            <div className="mt-6 px-5 py-4 rounded-2xl bg-green-50 border border-green-200 flex items-start gap-3">
              <Icons.Lightbulb className="w-5 h-5 text-brand-red shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">
                These newer models are built differently, so the back glass can be detached and replaced properly without needing to replace the entire frame.
              </p>
            </div>
          </div>

          {/* Eligible models card */}
          <div className="bg-white border-2 border-orange-200 rounded-3xl p-7 md:p-9 shadow-xl" data-testid="bg-newer-models-card">
            <div className="flex items-center justify-between mb-5">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 border border-orange-200 grid place-items-center">
                <Icons.Smartphone className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-orange-700 bg-orange-50 border border-orange-200 px-3 py-1 rounded-full">
                Glass-only OK
              </span>
            </div>
            <h4 className="font-display text-2xl font-black text-brand-navy tracking-tight">Back Glass Only Replacement<br />Available For:</h4>

            <div className="mt-6 grid grid-cols-2 gap-2">
              {GLASS_ONLY_MODELS.map((m) => (
                <div key={m} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                  <Icons.Check className="w-4 h-4 text-brand-red shrink-0" strokeWidth={3} />
                  <span className="text-sm font-bold text-brand-navy truncate">{m}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-slate-100 text-xs text-slate-500">
              Don't see your model? Call us — we'll confirm the right repair method for your specific iPhone.
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 md:mt-16 bg-gradient-to-br from-green-50 via-white to-orange-50 border border-slate-200 rounded-3xl p-8 md:p-10 text-center md:text-left flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-md" data-testid="bg-final-cta">
          <div>
            <h4 className="font-display text-2xl md:text-3xl font-black text-brand-navy tracking-tight">
              Not sure which repair your iPhone needs?
            </h4>
            <p className="mt-2 text-sm md:text-base text-slate-600 max-w-xl">
              Send us your iPhone model and a picture of the damage. We'll let you know the best repair option before you come in.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5 shrink-0 w-full md:w-auto">
            <QuoteButton testid="bg-quote-btn" onClick={onOpenQuote} variant="primary" size="md">
              Get a Free Back Glass Quote
            </QuoteButton>
            <CallButton testid="bg-call-btn" variant="secondary" size="md">
              Call {BUSINESS.phone}
            </CallButton>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * ModelSelector — interactive widget that lets a user pick their iPhone model
 * and instantly see whether they need:
 *   - "Whole Frame + Housing" (older models, factory-attached glass)
 *   - "Back Glass Only" (newer models, designed to be removable)
 * Selecting a model unlocks a "Get Quote for [model]" button that pre-fills the
 * Quote Dialog with the right service + device details.
 */
function ModelSelector({ onOpenQuote }) {
  const [model, setModel] = useState("");
  const verdict = useMemo(() => (model ? MODEL_REPAIR_MAP[model] : null), [model]);

  const isHousing = verdict === "housing";
  const isGlass = verdict === "glass";

  const buildPrefill = () => ({
    device_type: "iPhone",
    device_model: model,
    service_needed: "back_glass",
    issue: isHousing
      ? `Cracked back glass on my ${model}. Looking for the Whole Frame + Housing replacement (the recommended method for this model).`
      : `Cracked back glass on my ${model}. Looking for the Back Glass Only replacement.`,
  });

  return (
    <div
      data-testid="bg-model-selector"
      className="mt-8 md:mt-10 relative bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-md max-w-4xl mx-auto overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-green-100/70 blur-3xl pointer-events-none" aria-hidden />
      <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-orange-100/60 blur-3xl pointer-events-none" aria-hidden />

      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-red/10 border border-brand-red/20 mb-3">
          <Icons.Wand2 className="w-3.5 h-3.5 text-brand-red" />
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-brand-red">Quick Check</span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-black text-brand-navy tracking-tight leading-tight">
          Which iPhone do you have?
        </h3>
        <p className="text-sm md:text-base text-slate-600 mt-1.5 max-w-xl">
          Pick your model — we'll instantly tell you which back glass repair method is right for your iPhone.
        </p>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-stretch">
          <div className="relative">
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              data-testid="bg-model-select"
              aria-label="Select your iPhone model"
              className="w-full appearance-none bg-slate-50 border-2 border-slate-200 hover:border-slate-300 focus:border-brand-red focus:bg-white rounded-2xl px-5 py-4 pr-12 text-base font-bold text-brand-navy transition-all outline-none"
            >
              <option value="">Choose your iPhone model…</option>
              <optgroup label="iPhone 17 Series">
                {ALL_MODELS.filter((m) => m.startsWith("iPhone 17")).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </optgroup>
              <optgroup label="iPhone 16 Series">
                {ALL_MODELS.filter((m) => m.startsWith("iPhone 16")).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </optgroup>
              <optgroup label="iPhone 15 Series">
                {ALL_MODELS.filter((m) => m.startsWith("iPhone 15")).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </optgroup>
              <optgroup label="iPhone 14 Series">
                {ALL_MODELS.filter((m) => m.startsWith("iPhone 14")).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </optgroup>
              <optgroup label="iPhone 13 Series">
                {ALL_MODELS.filter((m) => m.startsWith("iPhone 13")).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </optgroup>
              <optgroup label="iPhone 12 Series">
                {ALL_MODELS.filter((m) => m.startsWith("iPhone 12")).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </optgroup>
              <optgroup label="iPhone 11 Series">
                {ALL_MODELS.filter((m) => m.startsWith("iPhone 11")).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </optgroup>
            </select>
            <Icons.ChevronDown className="w-5 h-5 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {model && (
            <button
              type="button"
              onClick={() => onOpenQuote && onOpenQuote(buildPrefill())}
              data-testid="bg-selector-quote-btn"
              className="inline-flex items-center justify-center gap-2 font-bold rounded-2xl px-6 py-4 bg-brand-red hover:brightness-110 text-white transition-all active:scale-95 whitespace-nowrap"
            >
              <Icons.MessageSquareQuote className="w-4 h-4" />
              Get Quote for {model.replace("iPhone ", "")}
            </button>
          )}
        </div>

        {/* Verdict card */}
        {verdict && (
          <div
            data-testid={`bg-verdict-${verdict}`}
            className={`mt-5 rounded-2xl p-5 md:p-6 border-2 transition-all ${
              isHousing
                ? "bg-green-50 border-brand-red/40"
                : "bg-orange-50 border-orange-300/60"
            }`}
          >
            <div className="flex items-start gap-4">
              <div
                className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${
                  isHousing ? "bg-brand-red text-white glow-red" : "bg-orange-500 text-white"
                }`}
              >
                {isHousing ? <Icons.Layers className="w-6 h-6" /> : <Icons.Sparkles className="w-6 h-6" />}
              </div>
              <div className="min-w-0">
                <div
                  className={`text-[11px] font-bold uppercase tracking-[0.18em] ${
                    isHousing ? "text-brand-red" : "text-orange-700"
                  }`}
                >
                  Recommended for your {model}
                </div>
                <h4 className="font-display text-xl md:text-2xl font-black text-brand-navy mt-1 tracking-tight leading-tight">
                  {isHousing
                    ? "Whole Frame + Housing Replacement"
                    : "Back Glass Only Replacement"}
                </h4>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                  {isHousing
                    ? `Your ${model} was built with the back glass factory-attached to the housing. The cleanest, strongest repair is to swap the entire original Apple housing — better fit, better finish, longer-lasting.`
                    : `Your ${model} was designed by Apple with a removable back glass. We can replace just the glass cleanly and reliably — no need to swap the full frame.`}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold ${
                      isHousing
                        ? "bg-white border border-brand-red/30 text-brand-red"
                        : "bg-white border border-orange-300 text-orange-700"
                    }`}
                  >
                    <Icons.CheckCircle2 className="w-3 h-3" />
                    {isHousing ? "Original Apple housing" : "Glass-only — Apple-approved build"}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-white border border-slate-200 text-slate-600">
                    <Icons.ShieldCheck className="w-3 h-3" />
                    90-day warranty
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!verdict && (
          <p className="mt-4 text-xs text-slate-500 flex items-center gap-2">
            <Icons.Info className="w-3.5 h-3.5" />
            Don't see your model? Just call {BUSINESS.phone} — we'll confirm the right repair in 30 seconds.
          </p>
        )}
      </div>
    </div>
  );
}
